import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';

import { Loader } from 'components';
import makeStyles from '@mui/styles/makeStyles';
import * as PDFJSViewer from 'pdfjs-dist/web/pdf_viewer.js';
import * as pdfjsLib from 'pdfjs-dist/webpack.js';
import 'pdfjs-dist/web/pdf_viewer.css';
import { useMountedState } from 'utils/hooks';

const useStyles = makeStyles(theme => ({
    container: {
        position: 'relative',
        overflow: 'auto',
        height: '68vh',
    },
    pdfViewer: {
        '& .page': {
            direction: 'ltr',
            margin: '10px 13px',
            border: `9px solid ${theme.palette.grey['A700']}`,
            backgroundColor: theme.palette.grey['A700'],
            [theme.breakpoints.down('md')]: {
                border: `2px solid ${theme.palette.grey['A700']}`,
            },
        },
    },
}));

const ERROR_MARGIN = 50; // pixels

const PDFViewer = ({ url, setReachBottom, containerClass }) => {
    const classes = useStyles();
    const viewerRef = useRef();
    const mounted = useMountedState();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!url) return; // prevent uncaught plugin error
        const eventBus = new PDFJSViewer.EventBus();

        const viewerContainer = document.getElementById('viewer');
        const pdfViewer = new PDFJSViewer.PDFViewer({
            container: viewerContainer,
            eventBus: eventBus,
        });

        eventBus.on('pagesinit', function () {
            if (mounted) pdfViewer.currentScaleValue = 'page-width';
        });
        const setScale = () => (pdfViewer.currentScaleValue = 'page-width');

        setLoading(true);
        const loadingTask = pdfjsLib.getDocument(url);
        loadingTask.promise
            .then(doc => {
                if (mounted) {
                    setLoading(false);
                    pdfViewer.setDocument(doc);
                    window.addEventListener('resize', setScale);
                }
            })
            .catch(reason => {
                setLoading(false);
                console.log(reason);
            });

        return () => {
            window.removeEventListener('resize', setScale);
        };
    }, [url, mounted]);

    const onScroll = useCallback(() => {
        if (viewerRef.current) {
            const { scrollTop, scrollHeight, offsetHeight } = viewerRef.current;
            if (Math.ceil(scrollTop + offsetHeight) >= scrollHeight - ERROR_MARGIN) {
                setReachBottom?.(true);
            }
        }
    }, [setReachBottom]);

    return (
        <div
            id="viewer"
            className={containerClass || classes.container}
            onScroll={onScroll}
            ref={viewerRef}
        >
            <div id="pdfViewer" className={classes.pdfViewer}></div>
            {loading && <Loader staticOnly />}
        </div>
    );
};

PDFViewer.propTypes = {
    url: PropTypes.string,
    containerClass: PropTypes.string,
    setReachBottom: PropTypes.func,
};

export default PDFViewer;
